import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef, EventEmitter,
  Inject,
  Input, OnChanges, OnDestroy, OnInit, Output, PLATFORM_ID,
} from '@angular/core';
import { WindowRef } from "@spartacus/core";
import { BREAKPOINT, BreakpointService, CarouselComponent, CarouselService } from '@spartacus/storefront';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  CustomCmsEventService
} from "src/app/spartacus/features/tracking/custom-events/checkout/cms/custom-cms-event.service";
import { CustomStorefrontService } from '../../../cms-structure/layout/custom-storefront/custom-storefront.service';
import { CustomCarouselService } from './custom-carousel.service';
import { Swipe } from './util/swiper';

@Component({
  selector: 'cx-carousel',
  templateUrl: './custom-carousel.component.html',
  styleUrls: ['./custom-carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CustomCarouselComponent extends CarouselComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  protected subscriptions: Subscription = new Subscription();
  @Input() hideButtons = false;
  @Input() hideIndicators = true;
  @Input() animation = 'fade-in-out';
  @Input() autoPlay = false;
  @Input() loop = false;
  @Input() delay = 5000;
  @Input() enableMouseActions = true;
  @Input() activeMainItem?: number;

  @Output() updateMainImage = new EventEmitter();

  @Input('items')
  set setItems(inputItems: Observable<any>[]) {
    this.items = inputItems;
    this.activeSlide = 0;
  }

  timer: any;

  isDown = false;
  startX = null;

  constructor(
    protected el: ElementRef,
    protected service: CarouselService,
    protected cd: ChangeDetectorRef,
    protected customService: CustomCarouselService,
    protected breakpointService: BreakpointService,
    protected customStorefrontService: CustomStorefrontService,
    protected winRef: WindowRef,
    protected customCmsEventService: CustomCmsEventService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    super(el, service);
  }

  ngOnChanges() {
    // disable OOTB ngOnChanges
  }

  ngAfterViewInit(): void {
    if (this.autoPlay) {
      this.loop = true;
      this.startInterval();
    }

    const carousel = this.el.nativeElement.querySelector('.carousel-panel.fade-in-out');
    if (carousel) {
      const swiper = new Swipe(carousel);
      swiper.onLeft(() => {
        this.updateActiveSlide(this.activeSlide + 1);
      });

      swiper.onRight(() => {
        this.updateActiveSlide(this.activeSlide - 1);
      });

      swiper.run();
    }

    this.customStorefrontService.setInfoForRegister();
  }

  updateActiveSlide(activeSlide): void {
    this.pause();
    const totalItems = this.items?.length;
    switch (true) {
      case activeSlide < 0: {
        this.activeSlide = totalItems - 1;
        break;
      }
      case activeSlide >= totalItems: {
        this.activeSlide = 0;
        break;
      }
      default:
        this.activeSlide = activeSlide;
    }
    this.cd.markForCheck();
    this.startInterval();
  }

  startInterval(): void {
    if (this.autoPlay) {
      this.play();
    }
  }

  play(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.timer = setTimeout(() => {
        this.updateActiveSlide(this.activeSlide + 1);
      }, (this.delay * 1000));
    }
  }

  pause(): void {
    if (this.autoPlay) {
      clearTimeout(this.timer);
    }
  }

  isDesktop(): Observable<boolean> {
    return this.breakpointService?.isUp(BREAKPOINT.md);
  }

  mouseDownSlide(event): boolean {
    event.preventDefault();
    if (!this.enableMouseActions) {
      return false;
    }

    this.isDown = true;

    this.startX = event.clientX;

    return false;
  }

  mouseUpSlide(event: MouseEvent, size: number): boolean {
    if (!this.enableMouseActions || !this.isDown) {
      return;
    }

    const totalItems = this.items?.length;

    const x = event.clientX - this.startX;

    let activeSlide = x > 5 ? this.activeSlide - size : (x < -5 ? this.activeSlide + size : this.activeSlide);

    if (activeSlide < 0) {
      activeSlide = 0;
    }

    if (activeSlide >= (totalItems - size)) {
      activeSlide = totalItems % size === 0 ? totalItems - size : totalItems - (totalItems % size);
    }

    this.activeSlide = activeSlide;

    this.isDown = false;
    this.startX = null;

    return false;
  }

  ngOnInit(): void {
    this.size$ = this.customService
      .getItemsPerSlide(this.el.nativeElement, this.itemWidth)
      .pipe(tap(() => (this.activeSlide = 0)));
  }

  arrowPrevious($event: Event, size: number): void {
    if (!!this.activeMainItem || this.activeMainItem === 0) {
      this.arrowsUpdateMainAction(this.activeMainItem - 1, size);
    } else {
      this.activeSlide = this.activeSlide - size;
    }
  }

  isPreviousDisabled(): boolean {
    if (!!this.activeMainItem || this.activeMainItem === 0) {
      return this.activeMainItem === 0;
    }
    return this.activeSlide === 0;
  }

  arrowNext($event: Event, size: number): void {
    if (!!this.activeMainItem || this.activeMainItem === 0) {
      this.arrowsUpdateMainAction(this.activeMainItem + 1, size);
    } else {
      this.activeSlide = this.activeSlide + size;
    }
  }

  isNextDisabled(size: number): boolean {
    if (!!this.activeMainItem || this.activeMainItem === 0) {
      return this.activeMainItem >= this.items?.length - 1;
    }
    return this.activeSlide > this.items?.length - size - 1;
  }

  arrowsUpdateMainAction(itemNumber: number, size: number): void {
    this.activeSlide = (itemNumber - (itemNumber % size));
    if (!!this.updateMainImage) {
      this.subscriptions.add(this.items[itemNumber].subscribe((item) => {
        this.updateMainImage.emit(item.container);
      }));
    }
  }

  handleClick(item) {
    const target = this.customCmsEventService.getSelectedComponent(item);
    // Only dispatch banner component
    if (target[0].typeCode) {
      this.customCmsEventService.dispatchSelectPromotionEvent(target);
    }
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
